import React, {useEffect, useMemo, useRef, useState} from 'react'
import {colors} from '@/constants'
import {Box, Stack, Typography} from '@mui/material'
import {keyframes, styled} from '@mui/system'
import {useAppDispatch, useAppSelector} from '@/hooks/redux'
import {selectClueForModal} from '@/store/selectors'
import useUpdateEffect from '@/hooks/useUpdateEffect'
import {ReactComponent as CongratulationsIcon} from '@/assets/svgs/CongratulationsIcon.svg'
import {ReactComponent as TerritoryXIcon} from '@/assets/svgs/TerritoryXIcon.svg'
import {ReactComponent as ErrorIcon} from '@/assets/svgs/ErrorIcon.svg'
import {useTranslation} from 'react-i18next'
import TransCustom from '@/components/UI/i18next'
import img11 from '@/assets/images/store/storeForLoi/img11.png'
import img8 from '@/assets/images/store/marketplace/img8.png'
import {SET_CLUE_FOR_MODAL} from '@/store/slices'
import {useNavigate} from 'react-router-dom'
import AdvisorsImage from '@/components/UI/AdvisorsImage'

const ComponentA = ({
  data,
}: {
  data: {isROrb?: boolean; count?: number; text?: string}
}) => (
  <Stack
    width="100%"
    height="100%"
    gap="20px"
    padding="10px 0"
    justifyContent="center"
    alignItems="center">
    <CongratulationsIcon />
    <TransCustom i18nKey="1230" color={colors.yellow} />
    <Typography color={colors.white} textAlign="center">
      <TransCustom
        i18nKey="1102"
        params={{
          param1: `${data.text && data.text.length > 10 ? data.text.slice(0, 10) + '...' : data.text || ''}`,
        }}
      />
      <br />
    </Typography>
  </Stack>
)

const ComponentB = ({data}: {data: {hexes?: string[]}}) => (
  <Stack
    gap="15px"
    alignItems="center"
    textAlign="center"
    fontSize="16px"
    padding="10px 0"
    justifyContent="center">
    <CongratulationsIcon />
    <Stack gap="5px" color={colors.yellow}>
      {data.hexes && data.hexes.length > 1 ? (
        <TransCustom
          params={{param1: data.hexes.length}}
          i18nKey="1432"
          style={{textDecoration: 'underline'}}
        />
      ) : (
        <TransCustom
          i18nKey="1044"
          params={{param1: data.hexes?.values().next().value || ''}}
        />
      )}
    </Stack>
    <TransCustom
      i18nKey="1351"
      variant="body2"
      color={colors.white}
      fontSize="16px"
    />
  </Stack>
)

const ComponentC = () => (
  <Stack
    gap="25px"
    alignItems="center"
    fontWeight="500"
    textAlign="center"
    fontSize="16px"
    justifyContent="center">
    <ErrorIcon width="35px" height="35px" />
    <TransCustom i18nKey="1045" color={colors.alert} />
    <TransCustom i18nKey="10451" color={colors.white} />
  </Stack>
)

const ComponentD = () => (
  <Stack
    gap="25px"
    padding="20px"
    alignItems="center"
    textAlign="center"
    justifyContent="center">
    <TerritoryXIcon />
    <TransCustom i18nKey="1352" variant="body2" color={colors.white} />
  </Stack>
)

const ComponentRentHex = ({data}: {data: {hexes?: string[]}}) => (
  <Stack
    gap="15px"
    alignItems="center"
    textAlign="center"
    fontSize="16px"
    padding="10px 0"
    justifyContent="center">
    <CongratulationsIcon />
    <Stack gap="5px" color={colors.yellow}>
      <TransCustom
        i18nKey="1543"
        params={{param1: data.hexes?.values().next().value || ''}}
      />
    </Stack>
    <TransCustom
      i18nKey="1544"
      variant="body2"
      color={colors.white}
      fontSize="16px"
    />
  </Stack>
)

const ComponentShop = () => {
  const navigate = useNavigate()
  const [isFlag, setIsFlag] = useState<string>('0')

  useEffect(() => {
    const isFlag = localStorage.getItem('isNotification')
    if (isFlag) {
      setIsFlag(isFlag)
      localStorage.setItem('isNotification', isFlag === '1' ? '0' : '1')
    } else {
      localStorage.setItem('isNotification', '0')
    }
  }, [])

  useEffect(() => {
    return () => {
      const time = localStorage.getItem('timeEndByNotification')
      if (!time) {
        localStorage.setItem(
          'timeEndByNotification',
          String(Date.now() + 900000)
        )
      }
    }
  }, [])

  return (
    <Stack alignItems="center" textAlign="center" gap="15px" padding="20px 0">
      <img src={isFlag === '0' ? img8 : img11} height="81px" width="175px" />
      <Typography variant="button">
        {isFlag === '0' ? 'Connect' : 'Open'} a{' '}
        <span
          onClick={() =>
            navigate(isFlag === '0' ? '/marketplace' : '/store-loi')
          }
          className="pointer"
          style={{textDecoration: 'underline', color: colors.orange}}>
          PERSONAL SHOP
        </span>
      </Typography>
      <Typography variant="body2">
        {isFlag === '0'
          ? 'for your Masters of Game structure'
          : 'for your structure of players'}
      </Typography>
    </Stack>
  )
}

const ComponentAdvisorHibernation = ({data}: {data: {name?: string}}) => {
  return (
    <Stack
      alignItems="center"
      width="70%"
      textAlign="center"
      gap="15px"
      padding="20px 0">
      <Stack width="60px">
        <AdvisorsImage
          advisor={{
            id: 0,
            is_hibernated: true,
            subscription_status: null,
            hex_count: 0,
          }}
        />
      </Stack>
      <TransCustom
        variant="body2"
        i18nKey="1558"
        color={colors.yellow}
        params={{param1: data.name || ''}}
      />
      <TransCustom variant="body2" width="90%" i18nKey="1559" />
    </Stack>
  )
}

const Index = () => {
  const {t} = useTranslation()
  const timeRef = useRef(3000)
  const dispatch = useAppDispatch()
  const selectedComponentIndex = useAppSelector(selectClueForModal)
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [isDisplay, setIsDisplay] = useState<boolean>(false)

  useUpdateEffect(() => {
    if (selectedComponentIndex.time) {
      timeRef.current = selectedComponentIndex.time
    } else {
      timeRef.current = 3000
    }
    setIsDisplay(true)
    setIsVisible(true)
  }, [selectedComponentIndex])

  const [remainingTime, setRemainingTime] = useState(0)

  // useEffect(() => {
  //   const endTimeEndByNotification = localStorage.getItem(
  //     'timeEndByNotification'
  //   )
  //   if (endTimeEndByNotification) {
  //     const currentTime = Date.now()
  //     const timeLeft = Number(endTimeEndByNotification) - currentTime
  //     if (timeLeft > 1000) {
  //       setRemainingTime((v) => timeLeft)
  //     } else {
  //       localStorage.removeItem('timeEndByNotification')
  //       dispatch(SET_CLUE_FOR_MODAL({index: 4, time: 30000, data: {}}))
  //     }
  //   } else {
  //     dispatch(SET_CLUE_FOR_MODAL({index: 4, time: 30000, data: {}}))
  //   }
  // }, [])

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setRemainingTime((prevTime) => {
  //       const time = prevTime - 1000
  //       if (time < 1000) {
  //         localStorage.removeItem('timeEndByNotification')
  //         dispatch(SET_CLUE_FOR_MODAL({index: 4, time: 30000, data: {}}))
  //         return 600000
  //       }
  //
  //       return time
  //     })
  //   }, 1000)
  //
  //   return () => clearInterval(interval)
  // }, [])

  useEffect(() => {
    if (!isVisible) {
      const timer = setTimeout(() => {
        setIsDisplay(false)
      }, 500)

      return () => clearTimeout(timer)
    } else {
      const closeTimer = setTimeout(() => {
        setIsVisible(false)
      }, timeRef.current)

      return () => clearTimeout(closeTimer)
    }
  }, [isVisible])

  const components = useMemo(
    () => [
      ComponentA,
      ComponentB,
      ComponentC,
      ComponentD,
      ComponentShop,
      ComponentRentHex,
      ComponentAdvisorHibernation,
    ],
    []
  )

  const ClueContent = useMemo(
    () => components[selectedComponentIndex.index],
    [components, selectedComponentIndex]
  )

  return isDisplay ? (
    <Slider
      isvisible={isVisible.toString()}
      width="500px"
      position="fixed"
      bottom="0"
      left="50%"
      zIndex="2000">
      <Stack
        borderRadius="20px 20px 0 0"
        border={`1px solid ${colors.lightBrown}`}
        bgcolor={colors.brown}
        width="30%"
        alignItems="center"
        padding="10px"
        className="pointer"
        onClick={() => setIsVisible(!isVisible)}>
        <svg
          width="30"
          height="30"
          viewBox="0 0 30 30"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_2956_10124)">
            <g filter="url(#filter0_d_2956_10124)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.03731 12.6982C5.11233 12.623 5.20145 12.5633 5.29956 12.5226C5.39768 12.4819 5.50286 12.4609 5.60909 12.4609C5.71532 12.4609 5.8205 12.4819 5.91862 12.5226C6.01673 12.5633 6.10585 12.623 6.18087 12.6982L15.3003 21.8192L24.4197 12.6982C24.5713 12.5466 24.777 12.4614 24.9914 12.4614C25.2059 12.4614 25.4116 12.5466 25.5632 12.6982C25.7149 12.8498 25.8 13.0555 25.8 13.27C25.8 13.4844 25.7149 13.6901 25.5632 13.8418L15.872 23.5329C15.797 23.6081 15.7079 23.6678 15.6098 23.7085C15.5117 23.7492 15.4065 23.7702 15.3003 23.7702C15.194 23.7702 15.0889 23.7492 14.9907 23.7085C14.8926 23.6678 14.8035 23.6081 14.7285 23.5329L5.03731 13.8418C4.9621 13.7667 4.90243 13.6776 4.86172 13.5795C4.82101 13.4814 4.80005 13.3762 4.80005 13.27C4.80005 13.1638 4.82101 13.0586 4.86172 12.9605C4.90243 12.8623 4.9621 12.7732 5.03731 12.6982Z"
                fill="#FFFDF0"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.03731 6.23726C5.11233 6.16205 5.20145 6.10238 5.29956 6.06167C5.39768 6.02096 5.50286 6 5.60909 6C5.71532 6 5.8205 6.02096 5.91862 6.06167C6.01673 6.10238 6.10585 6.16205 6.18087 6.23726L15.3003 15.3583L24.4197 6.23726C24.4947 6.16217 24.5839 6.10261 24.682 6.06198C24.7801 6.02134 24.8852 6.00042 24.9914 6.00042C25.0976 6.00042 25.2028 6.02134 25.3009 6.06198C25.399 6.10261 25.4881 6.16217 25.5632 6.23726C25.6383 6.31235 25.6979 6.40149 25.7385 6.4996C25.7791 6.5977 25.8 6.70285 25.8 6.80904C25.8 6.91523 25.7791 7.02038 25.7385 7.11849C25.6979 7.21659 25.6383 7.30573 25.5632 7.38082L15.872 17.072C15.797 17.1472 15.7079 17.2069 15.6098 17.2476C15.5117 17.2883 15.4065 17.3093 15.3003 17.3093C15.194 17.3093 15.0889 17.2883 14.9907 17.2476C14.8926 17.2069 14.8035 17.1472 14.7285 17.072L5.03731 7.38082C4.9621 7.3058 4.90243 7.21668 4.86172 7.11857C4.82101 7.02045 4.80005 6.91527 4.80005 6.80904C4.80005 6.70281 4.82101 6.59763 4.86172 6.49952C4.90243 6.4014 4.9621 6.31228 5.03731 6.23726Z"
                fill="#FFFDF0"
              />
            </g>
          </g>
          <defs>
            <filter
              id="filter0_d_2956_10124"
              x="-5.19995"
              y="-4"
              width="41"
              height="37.77"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset />
              <feGaussianBlur stdDeviation="5" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 1 0 0 0 0 0.945098 0 0 0 0 0.447059 0 0 0 0.2 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_2956_10124"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_2956_10124"
                result="shape"
              />
            </filter>
            <clipPath id="clip0_2956_10124">
              <rect width="30" height="30" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </Stack>
      <Stack
        position="relative"
        borderRadius="20px 20px 0 0"
        border={'1px solid ' + colors.lightBrown}
        alignItems="center"
        justifyContent="center"
        bgcolor={colors.brown}
        padding="10px"
        width="100%"
        height="fit-content"
        marginBottom="-10px">
        <ClueContent data={selectedComponentIndex.data} />
      </Stack>
    </Slider>
  ) : (
    <></>
  )
}

const slideUp = keyframes`
    from {
        transform: translate(-50%, 100%);
    }
    to {
        transform: translate(-50%, 0%);
    }
`

const slideDown = keyframes`
    from {
        transform: translate(-50%, 0%);
    }
    to {
        transform: translate(-50%, 100%);
    }
`

const Slider = styled(Box)(({isvisible}: {isvisible: string}) => ({
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  flexFlow: 'column',
  animation: `${isvisible === 'true' ? slideUp : slideDown} 0.5s forwards`,
}))

export default Index
